/**
 * Premium emoji packs API.
 *
 * Premium emojis are special Telegram custom_emoji entities — each carries
 * a `document_id` from a real emoji sticker pack installed on the user's
 * account. The backend resolves these via the user's Telethon session
 * (the webapp itself has no access to MTProto), so we *must* have a
 * connected account before the picker has any data to show.
 */

import { useQuery } from "@tanstack/react-query";

import { api, ApiError } from "./client";

export interface EmojiItem {
  document_id: string;
  unicode: string;
  keywords: string[];
  is_free: boolean;
}

export interface EmojiPack {
  set_id: string;
  access_hash: string;
  short_name: string;
  title: string;
  installed: boolean;
  emojis: EmojiItem[];
}

export interface EmojiPacksResponse {
  account_id: number;
  account_name: string;
  account_is_premium: boolean;
  packs: EmojiPack[];
}

export interface EmojiAsset {
  document_id: string;
  unicode: string;
  is_free: boolean;
  image_url: string | null;
  mime_type: string | null;
}

export interface EmojiAssetsResponse {
  assets: EmojiAsset[];
}

export interface EmojiLibraryItem {
  document_id: string;
  unicode: string;
  is_free: boolean;
  image_url: string | null;
  mime_type: string | null;
}

export interface EmojiLibraryResponse {
  emojis: EmojiLibraryItem[];
}

export type EmojiPacksState =
  | { kind: "ok"; data: EmojiPacksResponse }
  | { kind: "no-account" }
  | { kind: "flood"; retryAfter: number }
  | { kind: "error"; message: string }
  | { kind: "loading" };

export function useEmojiPacks(opts?: {
  accountId?: number;
  enabled?: boolean;
  refreshKey?: number;
}) {
  const accountId = opts?.accountId;
  return useQuery<EmojiPacksResponse, ApiError>({
    queryKey: ["emoji-packs", accountId ?? "default", opts?.refreshKey ?? 0],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (accountId !== undefined) qs.set("account_id", String(accountId));
      if ((opts?.refreshKey ?? 0) > 0) qs.set("refresh", "true");
      const tail = qs.toString();
      return api.get<EmojiPacksResponse>(`/emoji/packs${tail ? "?" + tail : ""}`);
    },
    enabled: opts?.enabled ?? true,
    retry: (_failureCount, err) => {
      // 412 → no account connected; never retry
      if (err instanceof ApiError && (err.status === 412 || err.status === 429)) {
        return false;
      }
      return _failureCount < 1;
    },
    staleTime: 9 * 60_000,
  });
}

export function useEmojiLibrary(opts?: { enabled?: boolean }) {
  return useQuery<EmojiLibraryResponse, ApiError>({
    queryKey: ["emoji-library"],
    queryFn: () => api.get<EmojiLibraryResponse>("/emoji/library"),
    enabled: opts?.enabled ?? true,
    retry: false,
    staleTime: 2 * 60_000,
  });
}

export function useEmojiAssets(opts?: {
  accountId?: number;
  documentIds?: string[];
  enabled?: boolean;
}) {
  const ids = Array.from(new Set(opts?.documentIds ?? [])).filter(Boolean).slice(0, 120);
  return useQuery<EmojiAssetsResponse, ApiError>({
    queryKey: ["emoji-assets", opts?.accountId ?? "default", ids],
    queryFn: () =>
      api.post<EmojiAssetsResponse>("/emoji/assets", {
        account_id: opts?.accountId ?? null,
        document_ids: ids,
      }),
    enabled: (opts?.enabled ?? true) && ids.length > 0,
    retry: false,
    staleTime: 60 * 60_000,
  });
}
